/* CNC CART — behaviour for the /cart page.
   Pairs with sections/cnc-cart.liquid and assets/cnc-cart.css.

   WHAT THIS FILE OWNS
   Quantity edits, removal, and re-rendering the section afterwards. That is
   all. The coupon row, the applied-coupon state, the points line, the totals
   breakdown and the recommendation rail are painted by cnc-app.js through
   window.CNC.paintBlocks — this file renders the mount points and hands over.

   WHY IT OWNS SO LITTLE
   The page works without it. Every control it touches is a real form control
   with a real destination: the steppers write to name="updates[]", Remove is a
   link to the cart change route, Checkout is a submit named "checkout". With
   scripts off all three still work, one page load at a time. This file's only
   job is to remove those page loads — so if it throws, or never loads, the
   worst outcome is a slower cart, never a broken one. Every entry point below
   is wrapped on that principle.

   THE ONE RULE
   After any change, RE-RENDER THE WHOLE SECTION. Never patch a row. Line
   numbers are positional and line keys are a hash that includes discount
   allocations, so a single removal renumbers everything below it and an
   automatic discount re-allocating can rewrite keys on rows nobody touched.
   Markup that disagrees with the server posts dead line numbers, Shopify
   answers 400, and the page appears frozen. One full swap costs a few
   kilobytes and cannot drift.                                               */

(function () {
  "use strict";

  var ROOT_SEL = "[data-cnc-cart]";
  var COUNT_SEL = ".cart-count";

  /* Long enough to collapse a burst of taps on the stepper, short enough that
     a customer who edits and immediately opens the bag sees the new number. */
  var DRAWER_SYNC_MS = 600;

  var busy = false;          // a change is in flight
  var suppress = false;      // ignore cart-change events we caused ourselves
  var syncTimer = null;

  function root() { return document.querySelector(ROOT_SEL); }

  /* ── Cart route ──────────────────────────────────────────────────────────
     Never hardcoded. Locale-prefixed stores serve the cart at /en-in/cart, and
     posting to /cart there redirects, drops the JSON Accept header and answers
     with a page — which surfaces as a JSON parse error a long way from the
     cause. The section prints the real route on the form's action. */
  function cartUrl() {
    var form = document.querySelector("[data-ck-form]");
    var action = form && form.getAttribute("action");
    return action ? action.replace(/\/+$/, "") : "/cart";
  }

  /* ── Header badge ────────────────────────────────────────────────────────
     The theme's badge is a digit plus a visually-hidden "4 items" label as
     separate child nodes. Flattening it with textContent renders "4 4 items"
     crammed into a 16px circle, and dropping the label costs screen-reader
     users the only place the count is spoken. So each node is rewritten in
     the shape it already has.

     Re-rendering the header section instead would be tidier markup and a much
     worse idea: the header binds its own listeners to its own nodes, and
     replacing it under them is how a working search box stops opening. */
  function syncCount(n) {
    var label = n + " item" + (n === 1 ? "" : "s");
    document.querySelectorAll(COUNT_SEL).forEach(function (el) {
      var wrote = false;
      Array.prototype.forEach.call(el.childNodes, function (node) {
        if (node.nodeType === 3 && /\d/.test(node.nodeValue)) {
          node.nodeValue = node.nodeValue.replace(/\d+/, String(n));
          wrote = true;
        } else if (node.nodeType === 1 && /^\s*\d+\s+items?\s*$/i.test(node.textContent)) {
          node.textContent = label;
          wrote = true;
        }
      });
      if (!wrote) el.textContent = String(n);
    });
  }

  /* ── Section re-render ───────────────────────────────────────────────────
     innerHTML on the live root, not outerHTML or replaceWith: the root is the
     node every delegated handler resolves against and the node paintBlocks
     was handed. Swapping the element itself leaves those pointing at a
     detached tree — the same trap that broke the drawer's stepper when its
     wrapper was replaced. */
  function swapSection(html) {
    var live = root();
    if (!html || !live) return false;
    var fresh = new DOMParser().parseFromString(html, "text/html").querySelector(ROOT_SEL);
    if (!fresh) return false;
    live.innerHTML = fresh.innerHTML;
    live.setAttribute("data-cart-count", fresh.getAttribute("data-cart-count") || "0");
    return true;
  }

  /* ── Re-read this section from the server ────────────────────────────────
     The one path every "something changed elsewhere" trigger goes through.

     It fetches through window.CNC.fetchSections, and that indirection is not
     ceremony. Written by hand, this request looked obviously correct and was
     silently useless: `/cart` answers `Accept: application/json` with the CART
     OBJECT and ignores ?sections= completely, so the response was a valid 200
     of valid JSON containing no markup. swapSection() found nothing, returned
     false, and the page kept displaying a cart that no longer existed — with
     no error anywhere. fetchSections() is where that knowledge lives now, so
     it can only be got wrong once. */
  function pullSection(failMsg) {
    if (busy || suppress) return;
    var live = root();
    var id = live && live.getAttribute("data-section-id");
    if (!id) return;
    var get = (window.CNC && window.CNC.fetchSections)
      ? window.CNC.fetchSections(cartUrl(), id)
      /* Older cnc-app.js on the page. Deliberately NOT a hand-rolled fetch
         with an Accept header — that is the bug this function exists for. */
      : Promise.reject(new Error("window.CNC.fetchSections missing — upload cnc-app.js"));
    return get
      .then(function (sections) { if (swapSection(sections[id])) return repaint(); })
      .catch(function (err) { console.error("[cnc-cart] " + failMsg, err); });
  }

  /* Repaint the shared blocks, then bring the phone bar back in line.

     paintBlocks rewrites the summary's breakdown once it knows the customer's
     tier — which is the only thing that decides delivery — so the bar has to
     be copied from the summary AFTER that lands, not from the server markup
     before it. */
  function repaint() {
    var live = root();
    if (!live) return Promise.resolve();
    var paint = (window.CNC && window.CNC.paintBlocks)
      ? window.CNC.paintBlocks(live)
      : Promise.resolve();
    return Promise.resolve(paint)
      .catch(function (err) { console.error("[cnc-cart] block paint failed", err); })
      .then(function () {
        // All three after paintBlocks, not before: it replaces the whole
        // breakdown, so anything done to those rows earlier is discarded — and
        // the server-rendered copy does not carry the coupon code.
        openTotalsByDefault(live);
        syncBar(live);
      });
  }

  /* The bar shows the same grand total and the same breakdown as the summary,
     because it IS the summary — copied, not recalculated. Two places deriving
     a total from the same cart is two places to get it wrong. */
  function syncBar(live) {
    var amt = live.querySelector("[data-ck-bar-amt]");
    var grand = live.querySelector(".cnc-ck-totals .cnc-total-amt");
    if (amt && grand && amt.textContent.trim() !== grand.textContent.trim()) {
      amt.textContent = grand.textContent.trim();
      bump(amt);
      bump(grand);
    }
    var panel = live.querySelector("[data-ck-bar-panel]");
    var rows = live.querySelector(".cnc-ck-totals [data-cnc-total-rows]");
    if (panel && rows) {
      /* The summary's copy is collapsible and carries [hidden]; the bar owns
         its own open state, so the clone must not inherit it. */
      var clone = rows.cloneNode(true);
      clone.hidden = false;
      clone.removeAttribute("data-cnc-total-rows");
      panel.innerHTML = "";
      panel.appendChild(clone);
    }
  }

  /* Restart-able, unlike a CSS transition — the total can change many times in
     a row and each one should register. */
  function bump(el) {
    el.classList.remove("is-bumped");
    void el.offsetWidth;                       // force a reflow so it replays
    el.classList.add("is-bumped");
  }

  /* Coupon confirmation — the toast, the confetti and the "did the code
     change?" bookkeeping — used to live here. It moved to cnc-app.js, because
     a coupon can be applied from the minicart drawer on any page in the store
     and this file only ships with /cart. See announceCoupon() there.

     Nothing replaced it here: paintBlocks calls it, and repaint() already
     awaits paintBlocks. */

  /* ── The change itself ───────────────────────────────────────────────────
     /cart/change.js by LINE, never by key. Both are accepted; only one of
     them is legible in markup that was rendered before an automatic discount
     re-allocated. Asking for the section in the same round trip is what keeps
     the two in step — request and repaint cannot interleave if they are the
     same response. */
  function change(line, quantity) {
    if (busy) return Promise.resolve();
    busy = true;

    var live = root();
    var id = live && live.getAttribute("data-section-id");
    var row = live && live.querySelector('[data-ck-line="' + line + '"]');
    if (row) row.classList.add("is-busy");

    return fetch(cartUrl() + "/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({
        line: line,
        quantity: quantity,
        sections: id ? [id] : [],
        sections_url: cartUrl()
      })
    })
      .then(function (r) {
        return r.json().then(function (data) {
          if (!r.ok) throw new Error(data.description || data.message || "Cart update failed");
          return data;
        });
      })
      .then(function (cart) {
        syncCount(cart.item_count);
        var swapped = id && cart.sections && swapSection(cart.sections[id]);
        /* No section in the response is not a failure worth guessing around.
           Reloading lands on a page rendered by the server, which is the state
           this function was trying to reach anyway. */
        if (!swapped) { location.reload(); return; }
        return repaint().then(scheduleDrawerSync);
      })
      .catch(function (err) {
        console.error("[cnc-cart] could not update the cart", err);
        if (row) row.classList.remove("is-busy");
        /* Put the input back to what the server last told us. Leaving a
           rejected number on screen is how someone checks out believing they
           ordered three of something. */
        var input = row && row.querySelector("[data-ck-qty-input]");
        if (input && input.dataset.was) input.value = input.dataset.was;
      })
      .then(function () { busy = false; });
  }

  /* The drawer renders its own copy of these lines, and a quantity change does
     NOT change a line key — so the drawer's self-healing key check cannot see
     the drift, and the bag would open showing the number from before the edit.
     A full page fetch is the only thing that fixes it, so it happens once,
     after the taps stop, off the critical path.

     suppress is not optional: refreshCart announces the change it just made,
     this file listens for exactly that announcement, and without the flag the
     two would take turns re-rendering each other. */
  function scheduleDrawerSync() {
    if (!window.CNC || !window.CNC.refreshCart) return;
    if (!document.querySelector(".minicart__wrapper")) return;   // no drawer here
    clearTimeout(syncTimer);
    syncTimer = setTimeout(function () {
      suppress = true;
      Promise.resolve(window.CNC.refreshCart())
        .catch(function (err) { console.error("[cnc-cart] drawer sync failed", err); })
        .then(function () { suppress = false; });
    }, DRAWER_SYNC_MS);
  }

  /* ── Controls ────────────────────────────────────────────────────────────
     All delegated on document, because the section is replaced wholesale after
     every change and anything bound to a node inside it would be bound to a
     node that no longer exists. */
  function initControls() {
    if (document.documentElement.dataset.cncCartCtl) return;
    document.documentElement.dataset.cncCartCtl = "1";

    document.addEventListener("click", function (e) {
      var live = root();
      if (!live) return;

      var step = e.target.closest("[data-ck-step]");
      if (step && live.contains(step)) {
        var box = step.closest("[data-ck-qty]");
        var input = box && box.querySelector("[data-ck-qty-input]");
        if (!input) return;
        var next = (parseInt(input.value, 10) || 0) + (parseInt(step.getAttribute("data-ck-step"), 10) || 0);
        if (next < 0) next = 0;
        input.dataset.was = input.value;
        input.value = next;
        change(parseInt(input.getAttribute("data-line"), 10), next);
        return;
      }

      var remove = e.target.closest("[data-ck-remove]");
      if (remove && live.contains(remove)) {
        /* preventDefault only once we are certain we can do the job. The href
           is a working fallback and swallowing the click without replacing it
           is worse than a page load. */
        e.preventDefault();
        change(parseInt(remove.getAttribute("data-ck-remove"), 10), 0);
        return;
      }

      var barToggle = e.target.closest("[data-ck-bar-toggle]");
      if (barToggle && live.contains(barToggle)) {
        var panel = live.querySelector("[data-ck-bar-panel]");
        if (!panel) return;
        var open = panel.hidden;
        panel.hidden = !open;
        barToggle.setAttribute("aria-expanded", String(open));
      }
    });

    /* Typed quantities commit on change, not on input: firing a request per
       keystroke turns "12" into a request for 1 and then a request for 12,
       and the first one can land second. */
    document.addEventListener("change", function (e) {
      var input = e.target.closest("[data-ck-qty-input]");
      var live = root();
      if (!input || !live || !live.contains(input)) return;
      var qty = parseInt(input.value, 10);
      if (isNaN(qty) || qty < 0) qty = 0;
      change(parseInt(input.getAttribute("data-line"), 10), qty);
    });

    /* Enter in a quantity field submits the form, which posts every
       updates[] at once and reloads the page — correct, but a page load for
       something we can do in place. */
    document.addEventListener("keydown", function (e) {
      if (e.key !== "Enter") return;
      var input = e.target.closest("[data-ck-qty-input]");
      var live = root();
      if (!input || !live || !live.contains(input)) return;
      e.preventDefault();
      input.blur();                              // fires the change handler above
    });
  }

  /* ── Savings count-up ────────────────────────────────────────────────────
     The final frame is the server-rendered string, restored verbatim — the
     animation never gets to decide what the number says. Intermediate frames
     use the platform's own en-IN grouping rather than a second copy of our
     money formatter, which is the only formatter allowed to produce a figure
     anyone acts on. */
  var DURATION = 620;

  function countUp(live) {
    var el = live.querySelector("[data-ck-saving]");
    if (!el || el.dataset.ckCounted) return;
    el.dataset.ckCounted = "1";

    /* The authoritative string is the server's, stashed before the first frame
       and restored from the stash — never re-read off the screen. */
    var final = el.textContent;
    var target = parseFloat(el.getAttribute("data-ck-saving"));

    /* A HARD FLOOR, set before anything is allowed to move.

       requestAnimationFrame does not run in a backgrounded tab. Measured on
       the live theme: a cart opened in a background tab froze mid-count and
       sat there reading ₹190 on a ribbon whose real value was ₹241.70 —
       an animation had quietly overwritten a money figure with a wrong one
       and then stopped. Anything can interrupt frames; nothing may be allowed
       to leave a false number on a cart page. So the truth is restored on a
       timer regardless of how the animation goes, and the animation is only
       ever allowed to write intermediate frames on top of that guarantee. */
    setTimeout(function () { el.textContent = final; }, DURATION + 500);

    if (document.hidden) return;
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    if (!isFinite(target) || target <= 0) return;

    var start = 0;
    function frame(now) {
      if (!start) start = now;
      var t = Math.min(1, (now - start) / DURATION);
      if (t < 1) {
        var eased = 1 - Math.pow(1 - t, 3);
        // toLocaleString('en-IN') is the platform's own lakh/crore grouping —
        // not a second copy of our money formatter, which stays the only thing
        // allowed to produce a figure anyone acts on.
        el.textContent = "₹" + Math.round(target * eased).toLocaleString("en-IN");
        requestAnimationFrame(frame);
      } else {
        el.textContent = final;
      }
    }
    requestAnimationFrame(frame);
  }

  /* ── The breakdown starts open here ──────────────────────────────────────
     The drawer collapses it because vertical space in a drawer is scarce.
     This page has a sticky column with room to spare, and the collapsed
     default meant a customer who had just applied a coupon could see the
     total change but not the line proving why — the discount was one tap
     away, inside an accordion nobody had a reason to suspect.

     Only when they have not decided for themselves: cnc-app.js persists an
     explicit collapse for the session, and overriding that every repaint
     would make the control feel broken. */
  function openTotalsByDefault(live) {
    var pref = null;
    try { pref = sessionStorage.getItem("cnc:totals-open"); } catch (e) { /* private mode */ }
    if (pref !== null) return;
    var rows = live.querySelector(".cnc-ck-totals [data-cnc-total-rows]");
    var head = live.querySelector(".cnc-ck-totals [data-cnc-total-toggle]");
    if (rows) rows.hidden = false;
    if (head) head.setAttribute("aria-expanded", "true");
  }

  /* ── Boot ────────────────────────────────────────────────────────────────
     cnc-app.js and this file are both deferred, so their execution order is
     document order and window.CNC is already defined. The optional chaining
     on every call into it is still deliberate: a section that loads only one
     of the two scripts should degrade to the server-rendered page, not throw
     on line one and leave the steppers unbound. */
  function boot() {
    var live = root();
    if (!live) return;
    initControls();
    countUp(live);
    repaint();
  }

  /* Registered once per document, not once per execution of this file.
     cnc-app.js is emitted by every CNC section and so runs several times per
     page; this file should only ever run once, but the day a second section
     loads it, two listeners would each re-render the section for every
     change. The guard costs one attribute. */
  if (window.CNC && window.CNC.onCartChange && !document.documentElement.dataset.cncCartSub) {
    document.documentElement.dataset.cncCartSub = "1";
    /* Fired by cnc-app.js after a coupon is applied or removed in the sheet,
       and after a wishlist-rail add. The cart has changed underneath us and
       the numbers on this page — the totals, the savings ribbon, the delivery
       meter — are all stale, so the section is re-rendered from the server
       rather than patched. */
    window.CNC.onCartChange(function () {
      pullSection("could not refresh after a coupon change");
    });
  }

  /* ── Catch-alls for a cart that moved somewhere else ─────────────────────
     onCartChange covers everything CNC does. Two things it cannot cover:

       · the THEME's own stepper, inside the minicart, on this very page. Its
         handlers are the theme's and announce nothing. Rather than try to
         hook them, the page re-reads itself when the drawer closes — that is
         the moment the customer's attention comes back here, and it costs one
         request only if the drawer was actually opened.
       · a back-navigation restored from the bfcache, which replays a page
         rendered before whatever happened in between. `persisted` is the
         whole point: a normal load has already rendered fresh markup. */
  function resync() { pullSection("resync failed"); }

  var drawerWasOpen = false;
  new MutationObserver(function () {
    var open = document.documentElement.classList.contains("open-minicart");
    if (drawerWasOpen && !open) resync();
    drawerWasOpen = open;
  }).observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });

  window.addEventListener("pageshow", function (e) { if (e.persisted) resync(); });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  // The theme editor re-renders a section on every settings change.
  document.addEventListener("shopify:section:load", boot);
})();
